import { useEffect } from 'react'

/** Deckkraft der Tönung — die der Toasts (`ToastCard.scss`, `space-glass(0.66)`). */
const TINT_ALPHA = 0.66

type Rgb = readonly [number, number, number]

/**
 * Eine CSS-Farbe (auch `oklch(from …)`) als sRGB-Werte — über einen Pixel im Canvas. Der Hauptprozess
 * versteht für `setBackgroundColor` nur Hex, `rgb()` und `hsl()`.
 */
function canvasRgb(color: string): Rgb | null {
  const ctx = document.createElement('canvas').getContext('2d', { willReadFrequently: true })
  if (!ctx) return null
  ctx.fillStyle = color
  ctx.fillRect(0, 0, 1, 1)
  const [r = 0, g = 0, b = 0] = ctx.getImageData(0, 0, 1, 1).data
  return [r, g, b]
}

/** Der Ton des Glases (`--menu-surface`) der Karte — ein Probe-Element löst die Variable auf. */
function readTint(card: HTMLElement, toRgb: (color: string) => Rgb | null): string | null {
  const surface = card.querySelector<HTMLElement>('.quick-add-card') ?? card
  const probe = document.createElement('span')
  probe.style.cssText = 'position:absolute;width:0;height:0;background-color:var(--menu-surface)'
  surface.appendChild(probe)
  const color = getComputedStyle(probe).backgroundColor
  probe.remove()
  const rgb = color ? toRgb(color) : null
  return rgb ? `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${TINT_ALPHA})` : null
}

/**
 * **Das Glas hinter der Karte folgt ihr** (Sven, 04.10.2026, Plan `quick-add-glass.md`). Auf dem Mac liegt
 * hinter der Karte ein eigenes Fenster aus Material; die Karte meldet ihm ihre Höhe — bei JEDER Änderung,
 * auch mitten im Auf- und Zufahren, sonst hinge das Glas hinterher — und den Ton ihres Lebensbereichs.
 *
 * Getrennt von `useQuickAddWindowHeight`: Das Inhaltsfenster wächst sofort und schrumpft erst nach der
 * Bewegung, das Glas folgt der Karte Bild für Bild.
 */
export function useQuickAddGlass(
  cardRef: React.RefObject<HTMLElement | null>,
  enabled: boolean,
  toRgb: (color: string) => Rgb | null = canvasRgb,
): void {
  useEffect(() => {
    const card = cardRef.current
    const send = window.windowAPI?.quickAddGlass
    if (!enabled || !card || !send) return

    let lastHeight = 0
    let lastTint: string | null = null
    const report = () => {
      const height = Math.round(card.getBoundingClientRect().height)
      // Eine Null misst ein verstecktes Fenster, keine Karte.
      if (height <= 0) return
      const tint = readTint(card, toRgb)
      if (height === lastHeight && tint === lastTint) return
      lastHeight = height
      lastTint = tint
      void send({ height, ...(tint ? { tint } : {}) }).catch(() => {})
    }

    const sizeObserver = new ResizeObserver(report)
    sizeObserver.observe(card)
    // Der Lebensbereich wechselt am `data-space` der Karte — dann wechselt der Ton.
    const spaceObserver = new MutationObserver(report)
    spaceObserver.observe(card, { subtree: true, attributes: true, attributeFilter: ['data-space'] })
    report()

    return () => {
      sizeObserver.disconnect()
      spaceObserver.disconnect()
    }
  }, [cardRef, enabled, toRgb])
}
